import { useCallback, useEffect, useRef } from "react"

import { useAppDispatch } from "@/core/app/hooks"
import type { AppDispatch } from "@/core/app/store"

import {
  cleanupScannerFiles,
  downloadScannerFile,
  loadScannerFiles,
} from "./integrationsThunkApi"
import { setAttachmentsLoading } from "./integrationsSlice"
import { setAlert } from "@/core/app/slices/alert/alertSlice"
import type { ScannerFileEntry, ScannerFileGroupKey } from "./integrations.type"

const POLL_INTERVAL_MS = 2000
const MAX_POLL_ATTEMPTS = 500 //Infinity

/**
 * Master toggle for all scanner import feedback toasts.
 * Controls success toasts ("Imported … from Scanner"),
 * error toasts ("Failed to load/download from Scanner"),
 * and info toasts ("No data found / timed out").
 * Set to false to suppress all of them.
 */
const SCANNER_IMPORT_TOAST_ENABLED = true
/** Set to false to suppress download-failure toasts specifically. */
const SCANNER_DOWNLOAD_ERROR_TOAST_ENABLED = true

// Shows which scan files were successfully imported; "info" severity when none found.
function dispatchScanImportToast(
  dispatch: AppDispatch,
  hasUpper: boolean,
  hasLower: boolean,
) {
  let message: string
  if (hasUpper && hasLower) {
    message = "Imported the available Upper & Lower scan from Scanner"
  } else if (hasUpper) {
    message = "Imported the available Upper scan from Scanner"
  } else if (hasLower) {
    message = "Imported the available Lower scan from Scanner"
  } else {
    message = "No scan data found in Scanner for the selected patient"
  }
  dispatch(setAlert({ message, severity: hasUpper || hasLower ? "success" : "info" }))
}

// Shows how many photos were imported; "info" severity when none found.
function dispatchPhotosImportToast(dispatch: AppDispatch, count: number) {
  if (count > 0) {
    dispatch(setAlert({
      message: `Imported ${count} available photo${count === 1 ? "" : "s"} from Scanner`,
      severity: "success",
    }))
  } else {
    dispatch(setAlert({
      message: "No image data found in Scanner for the selected patient",
      severity: "info",
    }))
  }
}

export interface ScannerFilesLoaderOptions {
  externalCaseId: string | number
  scannerName: string
  requiredKeys: ScannerFileGroupKey[]
  onUpperReady?: (file: File) => void
  onLowerReady?: (file: File) => void
  /** Called once when ALL photos are downloaded. Awaited before cleanup fires. */
  onPhotosReady?: (files: File[]) => void | Promise<void>
  onError?: () => void
}

export function useScannerFilesLoader() {
  const dispatch = useAppDispatch()
  // Incremented on each startLoading call or unmount to invalidate stale poll iterations.
  const pollSessionRef = useRef(0)
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const startLoading = useCallback(
    async ({
      externalCaseId,
      scannerName,
      requiredKeys,
      onUpperReady,
      onLowerReady,
      onPhotosReady,
      onError,
    }: ScannerFilesLoaderOptions) => {
      const session = ++pollSessionRef.current
      // Tracks attachment_ids already queued; prevents duplicate downloads across poll iterations.
      const downloadedIds = new Set<number>()
      const photoDownloadPromises: Promise<File | null>[] = []
      // Captured once when the server marks the file "completed"; awaited after the loop for toast.
      let upperFilePromise: Promise<File | null> | null = null
      let lowerFilePromise: Promise<File | null> | null = null

      // Transforms a completed file entry into a File download promise.
      // dispatch returns an action object, so .then() unwraps it to File | null.
      // On rejection, shows an error toast if both download and import toast flags are enabled.
      const queueDownload = (entry: ScannerFileEntry): Promise<File | null> => {
        if (downloadedIds.has(entry.attachment_id) || !entry.download_url) {
          return Promise.resolve(null)
        }
        downloadedIds.add(entry.attachment_id)
        return dispatch(
          downloadScannerFile({ url: entry.download_url, fileName: entry.name, scannerName }),
        ).then((action) => {
          if (downloadScannerFile.fulfilled.match(action)) return action.payload
          if (SCANNER_IMPORT_TOAST_ENABLED && SCANNER_DOWNLOAD_ERROR_TOAST_ENABLED) {
            dispatch(setAlert({ message: "Failed to download from Scanner", severity: "error" }))
          }
          return null
        })
      }

      dispatch(setAttachmentsLoading("pending"))

      try {
        let allServerComplete = false

        for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
          if (session !== pollSessionRef.current) break

          const result = await dispatch(
            loadScannerFiles({ externalCaseId, scannerName, isPolling: true }),
          ).unwrap()

          if (session !== pollSessionRef.current) break

          const { upper, lower, photos } = result.files

          // Queue downloads as soon as the server marks files "completed" (non-blocking).
          // !upperFilePromise / !lowerFilePromise guards ensure we only queue each once.
          if (requiredKeys.includes("upper") && upper?.status === "completed" && !upperFilePromise) {
            upperFilePromise = queueDownload(upper)
            upperFilePromise.then((file) => file && onUpperReady?.(file))
          }
          if (requiredKeys.includes("lower") && lower?.status === "completed" && !lowerFilePromise) {
            lowerFilePromise = queueDownload(lower)
            lowerFilePromise.then((file) => file && onLowerReady?.(file))
          }
          if (requiredKeys.includes("photos")) {
            photos
              .filter((p) => p.status === "completed")
              .forEach((p) => photoDownloadPromises.push(queueDownload(p)))
          }

          // Exit poll loop once all required file groups are "completed" on the server.
          allServerComplete = requiredKeys.every((key) => {
            if (key === "upper") return !upper || upper.status === "completed"
            if (key === "lower") return !lower || lower.status === "completed"
            if (key === "photos") return photos.every((p) => p.status === "completed")
            return true
          })

          if (allServerComplete) break

          await new Promise<void>((resolve) => {
            pollTimerRef.current = setTimeout(resolve, POLL_INTERVAL_MS)
          })
          pollTimerRef.current = null
        }

        if (!allServerComplete && isFinite(MAX_POLL_ATTEMPTS) && session === pollSessionRef.current) {
          dispatch(setAlert({ message: "Fetching data from scanner timed out.", severity: "info", timeout: 5000 }))
        }

        // Await in-flight scan downloads, then show import result toast.
        const hasScanKeys = requiredKeys.includes("upper") || requiredKeys.includes("lower")
        if (SCANNER_IMPORT_TOAST_ENABLED && hasScanKeys && allServerComplete) {
          const [upperFile, lowerFile] = await Promise.all([
            upperFilePromise ?? Promise.resolve(null),
            lowerFilePromise ?? Promise.resolve(null),
          ])
          dispatchScanImportToast(dispatch, upperFile instanceof File, lowerFile instanceof File)
        }

        // Await all photo downloads, invoke onPhotosReady, then show toast and cleanup.
        if (requiredKeys.includes("photos")) {
          let photosCount = 0
          if (onPhotosReady && photoDownloadPromises.length) {
            const settled = await Promise.all(photoDownloadPromises)
            const validFiles = settled.filter((f): f is File => f instanceof File)
            photosCount = validFiles.length
            if (validFiles.length) await onPhotosReady(validFiles)
          }
          if (SCANNER_IMPORT_TOAST_ENABLED) dispatchPhotosImportToast(dispatch, photosCount)
          // Cleanup runs after onPhotosReady resolves to avoid deleting files mid-upload.
          dispatch(cleanupScannerFiles({ externalCaseId, scannerName }))
        }

        dispatch(setAttachmentsLoading("succeeded"))
      } catch(err) {
        dispatch(setAttachmentsLoading("failed"))
        // loadScannerFiles rejection propagates here via .unwrap(); skip toast if session was
        // already invalidated (e.g. dialog closed mid-flight).
        if (SCANNER_IMPORT_TOAST_ENABLED && session === pollSessionRef.current) {
          dispatch(setAlert({ message: "Failed to load scanner data", severity: "error" }))
        }
        onError?.()
      }
    },
    [dispatch],
  )

  // Invalidate any in-flight poll session on unmount to prevent stale state updates.
  useEffect(() => {
    return () => {
      pollSessionRef.current += 1
      if (pollTimerRef.current !== null) {
        clearTimeout(pollTimerRef.current)
        pollTimerRef.current = null
      }
    }
  }, [])

  return { startLoading }
}
